import {
  ChevronLeft,
  ChevronRight,
  Close,
  ImageNotSupported,
} from '@mui/icons-material';
import {
  Grid,
  IconButton,
  styled,
  Typography,
  Modal,
  Box,
  useTheme,
  alpha,
} from '@mui/material';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import GenButton from './GenButton';
import 'swiper/swiper.min.css';

const PREFIX = 'PhotoPopUp';

const classes = {
  modalContainer: `${PREFIX}-modalContainer`,
  modalHeader: `${PREFIX}-modalHeader`,
  modalFooter: `${PREFIX}-modalFooter`,
  radioLabel: `${PREFIX}-radioLabel`,
  submitButton: `${PREFIX}-submitButton`,
  nextButton: `${PREFIX}-nextButton`,
  prevButton: `${PREFIX}-prevButton`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.modalContainer}`]: {
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    flexDirection: 'column',
  },

  [`& .${classes.modalHeader}`]: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(0, 0, 0, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  [`& .${classes.modalFooter}`]: {
    padding: theme.spacing(1, 2),
  },
  [`.${classes.nextButton}`]: {
    position: 'absolute',
    top: '50%',
    right: '0px',
    cursor: 'pointer',
    zIndex: '10',
    transform: 'translate(0, -22.5px)',
    background: 'rgba(255, 255, 255, 0.5)',
    borderRadius: '50%',
  },
  [`.${classes.prevButton}`]: {
    position: 'absolute',
    top: '50%',
    left: '0px',
    cursor: 'pointer',
    zIndex: '10',
    transform: 'translate(0, -22.5px)',
    background: 'rgba(255, 255, 255, 0.5)',
    borderRadius: '50%',
  },
}));

const modalBoxStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  boxShadow: 24,
  maxWidth: '795px',
  width: '100%',
  maxHeight: '100%',
};

const PhotoGalleryPopUp = ({
  images,
  initialSlide,
  open,
  handleModalState,
  isServicesModal = false,
  notes,
}) => {
  const { t } = useTranslation('workOrdersTable');
  const swiperRef = useRef();
  const theme = useTheme();
  const closeButtonStyle = {
    color: theme.palette.common.black,
    borderColor: theme.palette.grey[200],
    '&:hover': {
      borderColor: theme.palette.grey[200],
      backgroundColor: theme.palette.grey[200],
    },
  };
  return (
    <Modal
      open={open}
      onClose={handleModalState}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box sx={modalBoxStyle}>
        <StyledGrid container className={classes.modalContainer}>
          <Grid item>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              className={classes.modalHeader}
            >
              <Grid item>
                <Typography variant="subtitle2">
                  {isServicesModal
                    ? t('detail.photoGalleryPopUp.services.title')
                    : t('detail.photoGalleryPopUp.title')}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  aria-label="close"
                  onClick={handleModalState}
                  size="large"
                >
                  <Close />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                maxHeight: 'calc(100vh - 100px)',
                overflow: 'scroll',
                scrollbarWidth: 'none',
              }}
            >
              <Swiper
                initialSlide={initialSlide}
                slidesPerView={1}
                loop={true}
                onBeforeInit={swiper => {
                  swiperRef.current = swiper;
                }}
              >
                <Box className={classes.prevButton}>
                  <GenButton
                    iconForIconButton={
                      <ChevronLeft color="action" fontSize="large" />
                    }
                    onClick={() => swiperRef.current?.slidePrev()}
                    data-testid="photoGalleryPrevButton"
                  />
                </Box>

                {images?.length ? (
                  images?.map((el, i) => {
                    return (
                      <SwiperSlide key={i}>
                        <Box
                          component="img"
                          src={el}
                          alt="Car"
                          sx={{
                            maxWidth: { xs: '100%', sm: 'calc(100% - 120px)' },
                            height: '100%',
                            padding: { xs: '0px', sm: '40px 60px ' },
                          }}
                          data-testid={el}
                        />
                      </SwiperSlide>
                    );
                  })
                ) : (
                  <Box
                    sx={{
                      maxWidth: '680px',
                      width: '100%',
                      height: { xs: '300px', sm: '520px' },
                      backgroundColor: alpha(theme.palette.primary.light, 0.2),
                      margin: { xs: '0px', sm: '40px 60px ' },
                    }}
                  >
                    <Grid
                      container
                      sx={{
                        maxWidth: '680px',
                        width: '100%',
                        height: { xs: '300px', sm: '520px' },
                      }}
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                    >
                      <Grid item>
                        <ImageNotSupported color="primary" fontSize="large" />
                      </Grid>
                      <Grid item>
                        <Typography variant="subtitle2">
                          {t('detail.photoGalleryPopUp.noPhotos')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
                <Box className={classes.nextButton}>
                  <GenButton
                    iconForIconButton={
                      <ChevronRight color="action" fontSize="large" />
                    }
                    onClick={() => swiperRef.current.slideNext()}
                    data-testid="photoGalleryNextButton"
                  />
                </Box>
              </Swiper>
              {isServicesModal && (
                <Box pt={0} pb={2} pl={{ xs: 2, sm: 8 }} pr={{ xs: 2, sm: 8 }}>
                  <Grid container>
                    <Grid item>
                      <Typography variant="subtitle2">
                        {t('detail.photoGalleryPopUp.services.notes')}
                      </Typography>
                      <Typography variant="body2">
                        {notes ? notes : '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item>
            <Grid
              container
              justifyContent="flex-end"
              sx={{ pb: { xs: 1, sm: 2 }, pr: { xs: 1, sm: 2 }, pt: 1 }}
            >
              <GenButton
                text={t('detail.photoGalleryPopUp.button')}
                size="medium"
                sx={closeButtonStyle}
                onClick={handleModalState}
              />
            </Grid>
          </Grid>
        </StyledGrid>
      </Box>
    </Modal>
  );
};

export default PhotoGalleryPopUp;
